import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { PathBreadcrumb, labelFor } from "./path-breadcrumb";
import { CommandMenu } from "./command-menu";

const SECTION_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  clients: "Clients",
  catalogue: "Catalogue",
  billing: "Billing",
  team: "Team",
  analytics: "Analytics",
  audit: "Audit log",
  notifications: "Notifications",
  settings: "Settings",
};

/** SidebarInset header — SidebarTrigger + breadcrumb + command palette + bell. */
export function SiteHeader({
  platformName,
}: {
  platformName: string;
}): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const section = pathname.split("/").filter(Boolean)[1] ?? "dashboard";
  const label = labelFor(SECTION_LABELS, section);
  const navigate = useNavigate();
  const unread = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const count = unread.data?.count ?? 0;

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator
        orientation="vertical"
        className="mr-2 data-[orientation=vertical]:h-4"
      />
      <PathBreadcrumb rootLabel={platformName} sectionLabel={label} />
      <div className="ml-auto flex items-center gap-2">
        <CommandMenu />
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          title="Notifications"
          className="relative"
          onClick={() => void navigate({ to: "/provider/notifications" })}
        >
          <Bell className="size-5" />
          {count > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </div>
    </header>
  );
}
